import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { groups, searchableRoutes } from "../data";
import { assetUrl } from "../asset-url";
import { ArrowIcon, MenuIcon, SearchIcon } from "./Icons";

const nav = [
  ["Science", "/science"],
  ["Platform", "/platform"],
  ["Applications", "/applications"],
  ["Product Line", "/product-line"],
  ["Pipeline", "/pipeline"],
  ["Insights", "/insights"],
  ["About", "/about"],
];

const utilityLeft = [
  ["Publications", "/insights/resources"],
  ["Careers", "/careers"],
  ["Newsroom", "/insights"],
  ["Contact", "/contact"],
];

const utilityRight = [
  ["Investors", "/investors"],
  ["Support", "/contact"],
];

const groupLabels = {
  science: "Science",
  platform: "Platform",
  applications: "Applications",
  "product-line": "Product Line",
  pipeline: "Pipeline",
};

function getSectionNavigation(pathname) {
  const [groupKey, slug, articleSlug] = pathname.split("/").filter(Boolean);
  const group = groups[groupKey];

  if (group) {
    const item = slug ? group.items.find((candidate) => candidate.slug === slug) : null;

    if (item) {
      return {
        label: item.title,
        home: `/${groupKey}`,
        links: [
          ["Overview", "#overview"],
          ["Capabilities", "#capabilities"],
          ["Workflow", "#workflow"],
          ["Program fit", "#program-fit"],
          ["Evidence", "#evidence"],
        ],
      };
    }

    if (slug) return null;

    return {
      label: groupLabels[groupKey],
      home: `/${groupKey}`,
      links: [
        ["Overview", "#overview"],
        ["System", "#system"],
        ["Workflow", "#workflow"],
        [groupKey === "product-line" ? "Products" : "Explore", "#explore"],
        ["Principles", "#principles"],
      ],
    };
  }

  if (groupKey === "insights") {
    if (slug === "articles" && articleSlug) {
      const articleExists = searchableRoutes.some((item) => item.path === pathname);
      if (!articleExists) return null;

      return {
        label: "Insights",
        home: "/insights",
        links: [
          ["All articles", "/insights/articles"],
          ["Key takeaways", "#takeaways"],
          ["Article", "#article"],
          ["Evidence", "#article-evidence"],
        ],
      };
    }

    if (slug && !["articles", "resources", "events"].includes(slug)) return null;

    return {
      label: "Insights",
      home: "/insights",
      links: [
        ["Overview", "/insights"],
        ["Articles", "/insights/articles"],
        ["Resources", "/insights/resources"],
        ["Events", "/insights/events"],
      ],
    };
  }

  if (groupKey === "about") {
    if (slug && !["story", "leadership"].includes(slug)) return null;

    return {
      label: "About",
      home: "/about",
      links: [
        ["Overview", "/about"],
        ["Our Story", "/about/story"],
        ["Leadership", "/about/leadership"],
        ["Careers", "/careers"],
        ["Contact", "/contact"],
      ],
    };
  }

  return null;
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const menuButtonRef = useRef(null);
  const searchButtonRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sectionNavigation = getSectionNavigation(location.pathname);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", menuOpen || searchOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    if (!menuOpen && !searchOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        const focusTarget = searchOpen ? searchButtonRef.current : menuButtonRef.current;
        setSearchOpen(false);
        setMenuOpen(false);
        window.requestAnimationFrame(() => focusTarget?.focus());
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [menuOpen, searchOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    window.requestAnimationFrame(() => searchButtonRef.current?.focus());
  };

  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return searchableRoutes.slice(0, 6);
    return searchableRoutes
      .filter((item) => `${item.title} ${item.kind}`.toLowerCase().includes(normalized))
      .slice(0, 8);
  }, [query]);

  const submitSearch = (event) => {
    event.preventDefault();
    if (results[0]) navigate(results[0].path);
  };

  return (
    <>
      <header className={`site-header ${sectionNavigation ? "has-section-nav" : ""} ${menuOpen ? "has-open-menu" : ""}`}>
        <div className="utility-header" aria-label="Utility navigation">
          <nav>
            {utilityLeft.map(([label, path]) => <Link key={label} to={path}>{label}</Link>)}
          </nav>
          <nav>
            {utilityRight.map(([label, path]) => <Link key={label} to={path}>{label}</Link>)}
            <span className="utility-language" aria-label="Language: English">EN</span>
          </nav>
        </div>
        <div className="header-inner">
          <Link className="brand-link" to="/" aria-label="Medein home">
            <img src={assetUrl("/assets/medein-logo-light.svg")} alt="Medein" />
          </Link>

          <nav className="desktop-nav" aria-label="Primary navigation">
            {nav.map(([label, path]) => (
              <NavLink key={path} to={path}>
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="header-actions">
            <button
              ref={searchButtonRef}
              className="icon-button search-button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              aria-haspopup="dialog"
              aria-expanded={searchOpen}
            >
              <SearchIcon />
            </button>
            <Link className="button button-primary header-cta" to="/partnership">
              Partner With Us
            </Link>
            <button
              ref={menuButtonRef}
              className="icon-button menu-button"
              onClick={() => setMenuOpen((value) => !value)}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              <MenuIcon open={menuOpen} />
            </button>
          </div>
        </div>

        <div id="mobile-navigation" className={`mobile-navigation ${menuOpen ? "is-open" : ""}`}>
          <nav aria-label="Mobile navigation">
            {nav.map(([label, path], index) => (
              <NavLink key={path} to={path}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {label}
                <ArrowIcon />
              </NavLink>
            ))}
          </nav>
          <div className="mobile-utility">
            <Link to="/careers">Careers</Link>
            <Link to="/investors">Investors</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <Link className="button button-primary" to="/partnership">
            Partner With Us <ArrowIcon />
          </Link>
        </div>

        {sectionNavigation ? (
          <nav className="section-navigation" aria-label={`${sectionNavigation.label} navigation`}>
            <Link className="section-navigation-title" to={sectionNavigation.home}>
              {sectionNavigation.label}
            </Link>
            <div>
              {sectionNavigation.links.map(([label, path], index) => (
                <Link
                  className={
                    path.startsWith("#")
                      ? (location.hash === path || (!location.hash && index === 0) ? "is-current" : "")
                      : (location.pathname === path ? "is-current" : "")
                  }
                  to={path}
                  key={`${label}-${path}`}
                >
                  {label}
                </Link>
              ))}
            </div>
          </nav>
        ) : null}
      </header>

      {searchOpen ? (
        <div className="search-overlay" role="dialog" aria-modal="true" aria-label="Search Medein">
          <div className="search-panel">
            <div className="search-panel-head">
              <p>Search Medein</p>
              <button className="text-button" onClick={closeSearch}>
                Close
              </button>
            </div>
            <form className="search-form" onSubmit={submitSearch}>
              <SearchIcon />
              <input
                autoFocus
                type="search"
                placeholder="Search science, platform, products…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Search query"
              />
            </form>
            <div className="search-results" aria-live="polite">
              {results.length ? (
                results.map((result) => (
                  <Link key={result.path} to={result.path}>
                    <span>{result.kind}</span>
                    <strong>{result.title}</strong>
                    <ArrowIcon />
                  </Link>
                ))
              ) : (
                <p className="empty-state">No matching pages. Try a broader scientific or product term.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
